import { USE_OUT_OF_CORE_TILE_MANAGER, PER_FRAME_TASK_TIME_BUDGET_FACTOR, PER_FRAME_TASK_TIME_BUDGET_MAX } from "../wgs/globals";
import { OutOfCoreTileManager } from "../wgs/scene/out-of-core-tile-manager/OutOfCoreTileManager";
import { VIEWER_RENDER_TICK } from "./EventTypes";

/** @import { Viewer3DImpl } from "./Viewer3DImpl" */

/**
 * This class implements a main loop that regularly calls the tick functions in
 * all registered viewers and interleaves task processing between the ticking
 */
export class MainLoop {
  alreadyStarted = false;
  anyViewerReady = false;
  moreWorkToDoInExecuteTasks = true;
  firstInvocationThisFrame = true;
  /** @type {Viewer3DImpl[]} */viewers = [];
  alreadyTickedViewers = [];
  frameCounter = 0;
  lastTickFrames = [];
  lastTaskExecutionFrame = 0;
  channel = new MessageChannel();

  constructor() {
    if (globalThis.requestAnimationFrame) {
      this._animationFrame();
    }

    // We use a message channel to trigger the main loop, because this
    // way we can circumvent the 4ms minimum resolution of setTimeout
    this.channel.port1.onmessage = this._mainLoop.bind(this);
  }

  /**
   * Register a viewer with the main loop
   *
   * @param {Viewer3DImpl} viewer - The viewer to register
   */
  registerViewer(viewer) {
    this.viewers.push(viewer);

    if (!this.alreadyStarted) {
      this.alreadyStarted = true;

      // Start the main animation and task processing loop
      setTimeout(this._mainLoop.bind(this), 0);
    }
  }

  /**
   * Unregister a viewer with the main loop
   *
   * @param {Viewer3DImpl} viewer - The viewer to register
   */
  unregisterViewer(viewer) {
    // Remove the registered viewer from the list
    this.viewers.splice(this.viewers.indexOf(viewer), 1);
  }

  /**
   * Count the frames
   *
   * We use requestAnimation loop only to count frames. The actual main loop is triggered via
   * setTimeout to get a higher time resolution when waiting for the previous frame to finish.
   * To avoid rendering multiple times within a frame, we use the frameCounter to
   * determine if the viewer has already ticked in this frame.
   */
  _animationFrame() {
    this.frameCounter++;
    requestAnimationFrame(this._animationFrame.bind(this));
  }

  /**
   * Internal main loop function
   */
  _mainLoop() {
    for (let i = 0; i < this.viewers.length; i++) {
      const viewer = this.viewers[i];

      // Make sure, we don't execute the tick functions more often than once
      // per frame (even though we schedule this loop via setTimeout to get)
      // a better time resolution to find the next frame than possible via
      // requestAnimationFrame
      if ((this.lastTickFrames[i] ?? 0) < this.frameCounter) {
        let frameTicked = viewer._animLoop(this.moreWorkToDoInExecuteTasks);

        // Did the viewer _animloop function actually call tick?
        if (frameTicked) {

          viewer.api.dispatchEvent(VIEWER_RENDER_TICK);

          // When using multiple viewers, the concept of "executingTasksOnceEveryFrame"
          // is a bit fuzzy, because each of the viewers can have a different frame rate
          // and the viewers can potentially skip rendering if not needed. We therefore
          // detect a case, where one of the viewers has already ticked in or after the frame
          // where task process was triggered and now has ticked again.
          if (this.lastTickFrames[i] >= this.lastTaskExecutionFrame) {
            this.firstInvocationThisFrame = true;
            this.lastTaskExecutionFrame = this.frameCounter;
          }

          // Store the frame number, where the viewer ticked
          this.lastTickFrames[i] = this.frameCounter;
        }
      }
    }

    // Trigger task processing
    this.moreWorkToDoInExecuteTasks = this._executeTasks(this.firstInvocationThisFrame);
    this.firstInvocationThisFrame = false;

    if (this.viewers.length > 0) {
      // We wait a bit now before looping again.
      // If there were tasks done, we do more tasks for the default deadline of 4ms.
      // If there were no tasks done, we put a setTimeout of 4ms.
      // 4ms is the minimum resolution of recurring setTimeouts, see https://developer.mozilla.org/en-US/docs/Web/API/Window/setTimeout#reasons_for_delays_longer_than_specified
      if (this.moreWorkToDoInExecuteTasks) {
        this.channel.port2.postMessage('');
      } else {
        setTimeout(this._mainLoop.bind(this), 4);
      }
    } else {
      // No viewers registered, stop the main loop
      this.alreadyStarted = false;
    }
  }

  /**
   * Executes tasks in between the ticks of the viewers
   *
   * @param {boolean} firstInvocationThisFrame - true if this is the first invocation of the main loop this frame
   * @returns {boolean} - true if any tasks were executed
   */
  _executeTasks = function (firstInvocationThisFrame) {
    let tasksExecuted = false;
    // Execute task processing
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      // Use a small default task processing budget. If no viewer is ready, we
      // will repeatedly call this function, but don't want it to block for a long
      // time, as this would reduce the resolution to start the next frame
      let processingTime = 4;

      // The first time in a frame, we process for a longer time to make sure, that
      // we have at least a certain minimum time for task processing every frame
      if (firstInvocationThisFrame) {
        processingTime = Math.min(...this.viewers.map((x) => x._getMaxTaskProcessingTime() * PER_FRAME_TASK_TIME_BUDGET_FACTOR), PER_FRAME_TASK_TIME_BUDGET_MAX);
      }

      const beginTime = performance.now();
      const deadline = beginTime + processingTime;
      tasksExecuted = OutOfCoreTileManager.executeTasks(deadline);

    }

    return tasksExecuted;
  };
}

export const mainLoop = new MainLoop();